import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useAddLegalDocumentMutation } from "../api/projectsApi";

interface LegalDocumentForm {
  title: string;
  description: string;
  file: File | null;
}

interface AddLegalDocumentDialogProps {
  open: boolean;
  projectId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const MAX_FILE_SIZE = 40 * 1024 * 1024; // 40MB in bytes

const compressImage = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > height && width > 1920) {
          height = Math.round((height * 1920) / width);
          width = 1920;
        } else if (height > 1920) {
          width = Math.round((width * 1920) / height);
          height = 1920;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        // Convert to blob with quality adjustment
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          file.type,
          0.7 // Adjust quality (0.7 = 70% quality)
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
  });
};

const AddLegalDocumentDialog: React.FC<AddLegalDocumentDialogProps> = ({ 
  open, 
  projectId,
  onClose,
  onSuccess
}) => {
  const [documents, setDocuments] = useState<LegalDocumentForm[]>([
    { title: "", description: "", file: null }
  ]);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [addLegalDocument] = useAddLegalDocumentMutation();

  const handleDocumentChange = (index: number, field: keyof LegalDocumentForm, value: string | File) => {
    setDocuments(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
  };

  const handleFileChange = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setSubmissionError(`File size exceeds 40MB limit. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        return;
      }

      setIsCompressing(true);
      setSubmissionError(null);

      // Compress file if it's an image
      if (file.type.startsWith('image/')) {
        const compressedFile = await compressImage(file);
        handleDocumentChange(index, 'file', compressedFile);
      } else {
        handleDocumentChange(index, 'file', file);
      }
    } catch (error) {
      setSubmissionError(error instanceof Error ? error.message : 'Failed to process file');
    } finally {
      setIsCompressing(false);
    }
  };

  const addNewDocument = () => {
    setDocuments(prev => [...prev, { title: "", description: "", file: null }]);
  };

  const removeDocument = (index: number) => {
    if (documents.length > 1) {
      setDocuments(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmissionError(null);
      
      const validDocuments = documents.filter(doc => doc.title && doc.file);
      
      if (validDocuments.length === 0) {
        setSubmissionError("Please add at least one document with title and file");
        return;
      }

      const formData = new FormData();
      formData.append("project_id", projectId);
      
      // Add each document to the formData
      validDocuments.forEach((doc, index) => {
        formData.append(`documents[${index}][title]`, doc.title);
        formData.append(`documents[${index}][short_description]`, doc.description || "");
        formData.append(`documents[${index}][file_path]`, doc.file as File);
      });
      
      await addLegalDocument({
        id: projectId,
        body: formData
      }).unwrap();

      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error("Error adding legal documents:", err);
      setSubmissionError("Failed to add documents. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDocuments([{ title: "", description: "", file: null }]);
    setSubmissionError(null);
    setIsSubmitting(false);
    setIsCompressing(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add Legal Documents
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {documents.map((doc, index) => (
          <Box 
            key={index} 
            sx={{ 
              mb: 3, 
              p: 2, 
              border: '1px solid #eee', 
              borderRadius: 1,
              position: 'relative'
            }}
          >
            {index > 0 && (
              <IconButton
                size="small"
                onClick={() => removeDocument(index)}
                sx={{ position: 'absolute', top: 4, right: 4 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            
            <Typography variant="subtitle2" gutterBottom>
              Document {index + 1}
            </Typography>
            
            <TextField
              fullWidth
              margin="dense"
              label="Title"
              value={doc.title}
              onChange={(e) => handleDocumentChange(index, 'title', e.target.value)}
              required
            />
            
            <TextField
              fullWidth
              margin="dense"
              label="Description"
              multiline
              rows={2}
              value={doc.description}
              onChange={(e) => handleDocumentChange(index, 'description', e.target.value)}
            />
            
            <Button
              variant="outlined"
              component="label"
              sx={{ mt: 1 }}
              fullWidth
              disabled={isCompressing}
            >
              {isCompressing ? 'Compressing...' : 'Upload Document (PDF, Images)'}
              <input
                hidden
                accept=".pdf,.doc,.docx,image/*"
                type="file"
                onChange={(e) => handleFileChange(index, e)}
                disabled={isCompressing}
              />
            </Button>
            
            {doc.file && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {doc.file.name} ({(doc.file.size / (1024 * 1024)).toFixed(2)}MB)
              </Typography>
            )}
          </Box>
        ))}
        
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addNewDocument}
          fullWidth
          sx={{ mb: 2 }}
          disabled={isCompressing}
        >
          Add Another Document
        </Button>
        
        {submissionError && (
          <Alert severity="error" onClose={() => setSubmissionError(null)} sx={{ mt: 2 }}>
            {submissionError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting || isCompressing}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={documents.some(doc => !doc.title || !doc.file) || isSubmitting || isCompressing}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            minWidth: '120px',
            position: 'relative',
            '& .MuiCircularProgress-root': {
              position: 'absolute',
              left: '50%',
              marginLeft: '-12px',
            }
          }}
        >
          {isSubmitting ? 'Saving...' : 'Save Documents'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLegalDocumentDialog;